import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Info } from "lucide-react";
import { Overline } from "@/components/Brand";
import { generateRoadmapPdf } from "@/lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailRoadmap({ profile, meta, roadmap }) {
  const [email, setEmail] = useState(profile.email || "");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const send = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    try {
      const pdf = await generateRoadmapPdf({ profile: { ...profile, firstName: profile.firstName.trim() }, meta, roadmap, output: "base64" });
      await axios.post(`${API}/email-roadmap`, {
        email: email.trim(),
        first_name: profile.firstName.trim(),
        assessed_date: meta.date,
        assessed_time: meta.time,
        time_zone: meta.timeZone,
        lunar_phase: meta.lunar.name,
        pdf_base64: pdf.base64,
        placements: Object.values(roadmap.placements).map((p) => ({ placement: p.key, journey_id: p.journeyId, pathway_ids: p.sequence })),
      });
      setSentTo(email.trim());
      toast.success("Your roadmap is on its way to your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : "We couldn't send the email right now. Please download your PDF instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="email-roadmap-section" className="rounded-3xl p-6 sm:p-8 border border-[#C9A24A]/30 bg-white/70">
      <Overline>Optional</Overline>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-[#4A2A20] mt-2">Email My Goddess Journey Roadmap</h3>
      <p className="text-sm text-[#7D5A4B] mt-2">Receive your complete personalized roadmap PDF, the same one you can download, attached to an email.</p>
      <form onSubmit={send} className="mt-5 flex flex-col sm:flex-row gap-3">
        <input data-testid="email-roadmap-input" type="email" className="field sm:flex-1" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="email-roadmap-btn" type="submit" disabled={!valid || sending} className="btn-rose rounded-full px-7 py-3 text-sm font-medium inline-flex items-center justify-center gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {sending ? "Sending…" : "Email My Roadmap"}
        </button>
      </form>
      {sentTo && <p data-testid="email-sent-confirmation" className="text-sm text-[#B76E60] mt-3">Sent to {sentTo}. Please check your inbox (and spam folder).</p>}
      <p className="text-xs text-[#7D5A4B] mt-4 flex gap-2 leading-relaxed">
        <Info className="w-4 h-4 shrink-0 text-[#C9A24A]" />
        Goddess University does not store your answers or add you to any mailing list. To deliver this message, our email provider temporarily processes and may retain delivery information (such as your email address and the message) according to its own policies.
      </p>
    </section>
  );
}
